/**
 * Charge Form Slide-over Alpine.js Component
 *
 * Progressive disclosure form for raising fee or maintenance charges
 * via the Accounting::RaiseCharge service.
 * Uses server-side search for property/tenancy/contractor selection.
 */

// Vocabulary from the landlord's fee schedule. If one of these appears in the
// description of a tenant charge filed under "Other permitted payment", the
// user's own words disagree with the party they've picked — which is the one
// thing they don't already know. Kept deliberately tight: a warning that cries
// wolf on lawful charges is a warning nobody reads by the third time.
const LANDLORD_FEE_TERMS = [
  'rent guarantee', 'legal expenses', 'insurance', 'gas safety',
  'eicr', 'electrical installation', 'epc', 'energy performance',
  'fire risk', 'pat test', 'legionella', 'management fee',
  'landlord statement', 'section notice', 'eviction', 'inventory'
]

export function chargeFormSlideover() {
  return {
    // Slideover state
    open: false,
    zIndex: 100010,

    // Form state
    whoCharged: '',
    whoPaid: '',
    netAmountPounds: '',
    vatRate: '20',
    description: '',
    chargeDate: new Date().toISOString().split('T')[0],
    // Set when a raise_charge doorway card opened this form — travels with
    // the submit so the card resolves.
    proposedActionId: null,

    // Recurring charge state
    chargeMode: 'one_off', // 'one_off' or 'recurring'
    recurringFrequency: 'monthly', // 'monthly', 'weekly', or 'annual'
    recurringStartDate: new Date().toISOString().split('T')[0],
    recurringEndDate: '',

    // Property search
    propertySearch: '',
    propertyResults: [],
    searchingProperties: false,
    showPropertyDropdown: false,
    selectedProperty: null,

    // Tenancy search
    tenancySearch: '',
    tenancyResults: [],
    searchingTenancies: false,
    showTenancyDropdown: false,
    selectedTenancy: null,

    // Contractor (charged) search
    contractorSearch: '',
    contractorResults: [],
    searchingContractors: false,
    showContractorDropdown: false,
    selectedContractor: null,

    // Contractor (paid) search
    paidContractorSearch: '',
    paidContractorResults: [],
    searchingPaidContractors: false,
    showPaidContractorDropdown: false,
    selectedPaidContractor: null,

    // Pre-fill flags (when opened from entity slideover)
    landlordPrefill: false,
    landlordName: '',
    landlordProperties: [],
    tenancyPrefill: false,
    contractorPrefill: false,

    // Holding-deposit retention (charging a tenancy application). The money is
    // the applicant's, held under Schedule 2 of the Tenant Fees Act 2019, so
    // this mode carries a statutory ground and a notice deadline that no other
    // charge does.
    applicationPrefill: false,
    applicationId: null,
    applicationName: '',
    applicationHeldPence: 0,
    retentionGrounds: [],
    retentionGroundKey: '',
    retentionNoticeDueOn: null,

    // Property/tenancy info
    ownerships: [],
    recentCharges: [],
    loadingInfo: false,

    // The landlord behind a charged tenancy's property — target of the
    // "charge the landlord instead" hand-off.
    swapLandlord: null,

    // The counterparty for the third payee card, whichever way round we are:
    // the sitting tenants when a landlord is charged, the landlords when a
    // tenancy is.
    propertyTenancy: null,
    tenancyLandlordNames: '',

    // Set when the description contradicts the party being charged; cleared
    // once the user has stood by their choice.
    mismatchWarning: null,
    mismatchAcknowledged: false,

    // Submit state
    submitting: false,
    error: null,
    successMessage: null,

    // Source context (pre-fill from kebab menu)
    source: null,

    // Fee category dropdown
    feeCategories: [],
    feeCategoryId: '',
    loadingFeeCategories: false,

    openSlideover(detail) {
      const store = Alpine.store?.('slideovers')
      if (store?.getNextZIndex) {
        this.zIndex = store.getNextZIndex()
      }

      this.resetForm()
      this.open = true
      this.source = detail?.source || null

      if (store?.register) {
        store.register('charge-form', 'new', this)
      }

      this.applyPrefill()
      this.loadFeeCategories()

      if (detail?.chargeMode) {
        this.chargeMode = detail.chargeMode
      }

      // A raise_charge doorway card: field prefill on top of the entity
      // prefill, and the card's id so submitting resolves it.
      if (detail?.prefill) {
        this.proposedActionId = detail.proposed_action_id || null
        const p = detail.prefill
        if (p.net_amount_pence != null) this.netAmountPounds = (p.net_amount_pence / 100).toFixed(2)
        if (p.net_amount_pence > 0 && p.vat_amount_pence != null) {
          this.vatRate = String(Math.round((p.vat_amount_pence / p.net_amount_pence) * 100))
        }
        if (p.description) this.description = p.description
        if (p.date) this.chargeDate = p.date
        if (p.who_paid) this.whoPaid = p.who_paid
      }
    },

    close() {
      const store = Alpine.store?.('slideovers')
      if (store) {
        store.unregister('charge-form', 'new')
      }
      this.$nextTick(() => {
        this.open = false
      })
    },

    resetForm() {
      this.proposedActionId = null
      this.whoCharged = ''
      this.whoPaid = ''
      this.netAmountPounds = ''
      this.vatRate = '20'
      this.description = ''
      this.chargeDate = new Date().toISOString().split('T')[0]

      this.chargeMode = 'one_off'
      this.recurringFrequency = 'monthly'
      this.recurringStartDate = new Date().toISOString().split('T')[0]
      this.recurringEndDate = ''

      this.propertySearch = ''
      this.propertyResults = []
      this.showPropertyDropdown = false
      this.selectedProperty = null

      this.tenancySearch = ''
      this.tenancyResults = []
      this.showTenancyDropdown = false
      this.selectedTenancy = null

      this.contractorSearch = ''
      this.contractorResults = []
      this.showContractorDropdown = false
      this.selectedContractor = null

      this.paidContractorSearch = ''
      this.paidContractorResults = []
      this.showPaidContractorDropdown = false
      this.selectedPaidContractor = null

      this.landlordPrefill = false
      this.landlordName = ''
      this.landlordProperties = []
      this.tenancyPrefill = false
      this.contractorPrefill = false

      this.applicationPrefill = false
      this.applicationId = null
      this.applicationName = ''
      this.applicationHeldPence = 0
      this.retentionGrounds = []
      this.retentionGroundKey = ''
      this.retentionNoticeDueOn = null

      this.ownerships = []
      this.recentCharges = []
      this.swapLandlord = null
      this.propertyTenancy = null
      this.tenancyLandlordNames = ''
      this.error = null
      this.successMessage = null
      this.submitting = false
      this.mismatchWarning = null
      this.mismatchAcknowledged = false

      this.feeCategoryId = ''
    },

    // Hand off to the landlord's composer rather than offering a payer toggle
    // inside this one: a charge still has exactly one origin, so there's never
    // any ambiguity about where it was raised from, but reaching the party you
    // actually meant no longer costs a trip out to another account.
    chargeLandlordInstead() {
      if (!this.swapLandlord) return

      window.dispatchEvent(new CustomEvent('open-charge-form-slideover', {
        detail: { source: { type: 'landlord', id: this.swapLandlord.id, name: this.swapLandlord.name } }
      }))
    },

    // The party being charged, in the vocabulary FeeCategory#payable_by uses.
    // 'property' means the property's landlord(s) bear the charge.
    get payerType() {
      if (this.whoCharged === 'property') return 'landlord'
      if (this.whoCharged === 'tenancy') return 'tenancy'
      if (this.whoCharged === 'contractor') return 'contractor'
      return ''
    },

    // Categories are fetched for the payer, so the landlord fee schedule is
    // never offered on a tenant charge. RaiseCharge enforces the same rule
    // server-side — this is the half that stops it being a decision at all.
    async loadFeeCategories() {
      this.loadingFeeCategories = true
      try {
        const payer = this.payerType
        const url = payer
          ? `/accounting/accounts/charges/fee_categories.json?payer=${payer}`
          : '/accounting/accounts/charges/fee_categories.json'
        const response = await fetch(url, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
        })
        if (response.ok) {
          this.feeCategories = await response.json()
        }
      } catch (e) {
        console.error('Failed to load fee categories:', e)
      } finally {
        this.loadingFeeCategories = false
      }
    },

    applyPrefill() {
      if (!this.source) return

      const { type, id } = this.source

      switch (type) {
        case 'tenancy':
          this.whoCharged = 'tenancy'
          this.tenancyPrefill = true
          this.$nextTick(async () => {
            try {
              const response = await fetch(`/accounting/accounts/tenancies/${id}.json`, {
                headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
              })
              if (response.ok) {
                const data = await response.json()
                this.selectedTenancy = {
                  id: id,
                  name: data.tenancy.property_name,
                  detail: data.tenancy.tenant_names
                }
                this.tenancySearch = data.tenancy.property_name
                this.onTenancySelected()
              }
            } catch (e) {
              console.error('Failed to pre-fill tenancy:', e)
            }
          })
          break
        case 'landlord':
          this.whoCharged = 'property'
          this.landlordPrefill = true
          this.landlordName = this.source.name || ''
          this.$nextTick(async () => {
            try {
              const response = await fetch(`/accounting/accounts/charges/landlord_properties.json?landlord_id=${id}`, {
                headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
              })
              if (response.ok) {
                this.landlordProperties = await response.json()
                if (this.landlordProperties.length === 1) {
                  this.selectLandlordProperty(this.landlordProperties[0].id)
                }
              }
            } catch (e) {
              console.error('Failed to fetch landlord properties:', e)
            }
          })
          break
        case 'tenancy_application':
          this.whoCharged = 'tenancy_application'
          this.whoPaid = 'agency'
          this.applicationPrefill = true
          this.applicationId = id
          this.applicationName = this.source.name || ''
          this.chargeMode = 'one_off'
          this.$nextTick(async () => {
            try {
              const response = await fetch(`/tenancy-applications/${id}/holding_deposit_summary`, {
                headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
              })
              if (response.ok) {
                const data = await response.json()
                this.applicationHeldPence = data.held_pence || 0
                this.retentionGrounds = data.grounds || []
                this.retentionNoticeDueOn = data.notice_due_on
                // Feeds the Landlord payee card — a retention can go to the
                // agency or be passed to the landlord, and nowhere else.
                this.tenancyLandlordNames = data.landlord_names || ''
              }
            } catch (e) {
              console.error('Failed to load holding deposit summary:', e)
            }
          })
          break
        case 'contractor':
          this.whoCharged = 'contractor'
          this.whoPaid = 'agency'
          this.contractorPrefill = true
          this.$nextTick(async () => {
            // Fetch contractor name for display
            try {
              const response = await fetch(`/accounting/accounts/contractors/${id}.json`, {
                headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
              })
              if (response.ok) {
                const data = await response.json()
                this.selectedContractor = {
                  id: id,
                  name: data.contractor?.name || `Contractor #${id}`
                }
                this.contractorSearch = this.selectedContractor.name
              }
            } catch (e) {
              console.error('Failed to pre-fill contractor:', e)
            }
          })
          break
      }
    },

    onWhoChargedChanged() {
      // The category list belongs to the payer, so a changed payer invalidates
      // both the selection and the options behind it.
      this.feeCategoryId = ''
      this.loadFeeCategories()

      this.selectedProperty = null
      this.propertySearch = ''
      this.propertyResults = []
      this.showPropertyDropdown = false

      this.selectedTenancy = null
      this.tenancySearch = ''
      this.tenancyResults = []
      this.showTenancyDropdown = false

      this.selectedContractor = null
      this.contractorSearch = ''
      this.contractorResults = []
      this.showContractorDropdown = false

      this.whoPaid = ''
      this.selectedPaidContractor = null
      this.paidContractorSearch = ''
      this.paidContractorResults = []
      this.showPaidContractorDropdown = false

      this.ownerships = []
      this.recentCharges = []
      this.netAmountPounds = ''
      this.vatRate = '20'

      // Contractor charged can only pay agency
      if (this.whoCharged === 'contractor') {
        this.whoPaid = 'agency'
      }
    },

    // --- Property search ---
    async searchProperties() {
      const query = this.propertySearch
      if (!query || query.length < 2) {
        this.propertyResults = []
        this.showPropertyDropdown = false
        return
      }
      this.searchingProperties = true
      try {
        const response = await fetch(`/accounting/accounts/charges/search_properties.json?q=${encodeURIComponent(query)}`, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
        })
        if (response.ok) {
          this.propertyResults = await response.json()
          this.showPropertyDropdown = true
        }
      } catch (e) {
        console.error('Failed to search properties:', e)
      } finally {
        this.searchingProperties = false
      }
    },

    selectProperty(property) {
      this.selectedProperty = property
      this.propertySearch = property.name
      this.propertyResults = []
      this.showPropertyDropdown = false
      this.onPropertySelected()
    },

    clearProperty() {
      this.selectedProperty = null
      this.propertySearch = ''
      this.ownerships = []
      this.recentCharges = []
    },

    async selectLandlordProperty(propertyId) {
      if (!propertyId) {
        this.selectedProperty = null
        this.ownerships = []
        this.recentCharges = []
        return
      }
      this.loadingInfo = true
      try {
        const response = await fetch(`/accounting/accounts/charges/property_info.json?property_id=${propertyId}`, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
        })
        if (response.ok) {
          const data = await response.json()
          this.selectedProperty = data.property
          this.ownerships = data.ownerships || []
          this.recentCharges = data.recent_charges || []
          this.propertyTenancy = data.tenancy || null
        }
      } catch (e) {
        console.error('Failed to fetch property info:', e)
      } finally {
        this.loadingInfo = false
      }
    },

    async onPropertySelected() {
      if (!this.selectedProperty) return
      this.loadingInfo = true
      try {
        const response = await fetch(`/accounting/accounts/charges/property_info.json?property_id=${this.selectedProperty.id}`, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
        })
        if (response.ok) {
          const data = await response.json()
          this.ownerships = data.ownerships || []
          this.recentCharges = data.recent_charges || []
          this.propertyTenancy = data.tenancy || null
        }
      } catch (e) {
        console.error('Failed to fetch property info:', e)
      } finally {
        this.loadingInfo = false
      }
    },

    // --- Tenancy search ---
    async searchTenancies() {
      const query = this.tenancySearch
      if (!query || query.length < 2) {
        this.tenancyResults = []
        this.showTenancyDropdown = false
        return
      }
      this.searchingTenancies = true
      try {
        const response = await fetch(`/accounting/accounts/charges/search_tenancies.json?q=${encodeURIComponent(query)}`, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
        })
        if (response.ok) {
          this.tenancyResults = await response.json()
          this.showTenancyDropdown = true
        }
      } catch (e) {
        console.error('Failed to search tenancies:', e)
      } finally {
        this.searchingTenancies = false
      }
    },

    selectTenancy(tenancy) {
      this.selectedTenancy = tenancy
      this.tenancySearch = tenancy.name
      this.tenancyResults = []
      this.showTenancyDropdown = false
      this.onTenancySelected()
    },

    clearTenancy() {
      this.selectedTenancy = null
      this.tenancySearch = ''
      this.recentCharges = []
    },

    async onTenancySelected() {
      if (!this.selectedTenancy) return
      this.loadingInfo = true
      try {
        const response = await fetch(`/accounting/accounts/charges/tenancy_info.json?tenancy_id=${this.selectedTenancy.id}`, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
        })
        if (response.ok) {
          const data = await response.json()
          this.recentCharges = data.recent_charges || []
          this.swapLandlord = data.landlord || null
          this.tenancyLandlordNames = data.landlord_names || ''
        }
      } catch (e) {
        console.error('Failed to fetch tenancy info:', e)
      } finally {
        this.loadingInfo = false
      }
    },

    // --- Contractor (charged) search ---
    async searchContractors() {
      const query = this.contractorSearch
      if (!query || query.length < 2) {
        this.contractorResults = []
        this.showContractorDropdown = false
        return
      }
      this.searchingContractors = true
      try {
        const response = await fetch(`/accounting/accounts/charges/search_contractors.json?q=${encodeURIComponent(query)}`, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
        })
        if (response.ok) {
          this.contractorResults = await response.json()
          this.showContractorDropdown = true
        }
      } catch (e) {
        console.error('Failed to search contractors:', e)
      } finally {
        this.searchingContractors = false
      }
    },

    selectContractor(contractor) {
      this.selectedContractor = contractor
      this.contractorSearch = contractor.name
      this.contractorResults = []
      this.showContractorDropdown = false
    },

    clearContractor() {
      this.selectedContractor = null
      this.contractorSearch = ''
    },

    // --- Contractor (paid) search ---
    async searchPaidContractors() {
      const query = this.paidContractorSearch
      if (!query || query.length < 2) {
        this.paidContractorResults = []
        this.showPaidContractorDropdown = false
        return
      }
      this.searchingPaidContractors = true
      try {
        const response = await fetch(`/accounting/accounts/charges/search_contractors.json?q=${encodeURIComponent(query)}`, {
          headers: { 'Accept': 'application/json', 'X-CSRF-Token': this.csrfToken }
        })
        if (response.ok) {
          this.paidContractorResults = await response.json()
          this.showPaidContractorDropdown = true
        }
      } catch (e) {
        console.error('Failed to search contractors:', e)
      } finally {
        this.searchingPaidContractors = false
      }
    },

    selectPaidContractor(contractor) {
      this.selectedPaidContractor = contractor
      this.paidContractorSearch = contractor.name
      this.paidContractorResults = []
      this.showPaidContractorDropdown = false
    },

    clearPaidContractor() {
      this.selectedPaidContractor = null
      this.paidContractorSearch = ''
    },

    get selectedFeeCategory() {
      return this.feeCategories.find(fc => String(fc.id) === String(this.feeCategoryId)) || null
    },

    // The backstop on the hole we left on purpose. Once categories are filtered
    // by payer, "Other permitted payment" is the only remaining route by which
    // a landlord's charge can reach a tenant's account — so it is the only
    // place worth interrupting.
    payerMismatch() {
      if (this.payerType !== 'tenancy') return null
      if (this.selectedFeeCategory?.slug !== 'other_permitted_payment') return null

      const description = (this.description || '').toLowerCase()
      const term = LANDLORD_FEE_TERMS.find(t => description.includes(t))
      return term ? { term: term } : null
    },

    // Standing by the charge dismisses the warning for this attempt only.
    confirmDespiteMismatch() {
      this.mismatchAcknowledged = true
      this.mismatchWarning = null
      this.submit()
    },

    // --- Submit ---
    async submit() {
      if (!this.canSubmit || this.submitting) return

      if (!this.mismatchAcknowledged) {
        const mismatch = this.payerMismatch()
        if (mismatch) {
          this.mismatchWarning = mismatch
          return
        }
      }

      this.submitting = true
      this.error = null
      this.successMessage = null

      try {
        if (this.applicationPrefill) {
          await this.submitRetention()
        } else if (this.chargeMode === 'recurring') {
          await this.submitRecurring()
        } else {
          await this.submitOneOff()
        }
      } catch (e) {
        console.error('Failed to create charge:', e)
        this.error = e.message || 'Failed to apply charge. Please try again.'
      } finally {
        this.submitting = false
      }
    },

    // Retentions go through the application's own endpoint rather than the
    // charges controller: the statutory ground, the held-amount ceiling and the
    // notice deadline are enforced on the model, so every door into this obeys
    // them — not just this form.
    async submitRetention() {
      const response = await fetch(`/tenancy-applications/${this.applicationId}/retain_holding_deposit`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify({
          ground_key: this.retentionGroundKey,
          net_amount_pence: this.netAmountPence,
          vat_amount_pence: this.vatAmountPence,
          payee: this.whoPaid,
          description: this.description
        })
      })

      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.error || 'Failed to apply the charge.')

      const remaining = this.formatPence(data.remaining_pence || 0)
      this.successMessage = `Charge applied. ${remaining} of the holding deposit remains repayable.`
      this.applicationHeldPence = data.remaining_pence || 0

      // Same announcement every other charge makes. A retention is still a
      // charge on an account, and the account behind this panel has to hear
      // about it the moment it lands.
      window.dispatchEvent(new CustomEvent('charge-created', { detail: data }))

      window.dispatchEvent(new CustomEvent('toast', {
        detail: {
          message: `Charge applied. Written reasons are due by ${data.notice_due_on || 'the statutory deadline'}.`,
          type: 'success'
        }
      }))

      setTimeout(() => { this.open = false }, 1800)
    },

    async submitOneOff() {
      const body = {
        charge: {
          who_charged: this.whoCharged,
          who_paid: this.whoPaid,
          property_id: this.selectedProperty?.id || null,
          tenancy_id: this.selectedTenancy?.id || null,
          contractor_id: this.selectedPaidContractor?.id || null,
          contractor_id_charged: this.whoCharged === 'contractor' ? this.selectedContractor?.id : null,
          net_amount_pence: this.netAmountPence,
          vat_amount_pence: this.vatAmountPence,
          description: this.description,
          date: this.chargeDate,
          fee_category_id: this.feeCategoryId || null
        }
      }
      if (this.proposedActionId) body.proposed_action_id = this.proposedActionId

      const response = await fetch('/accounting/accounts/charges.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create charge')
      }

      this.successMessage = `Charge applied successfully (${data.charges_created} charge${data.charges_created > 1 ? 's' : ''} created)`
      window.dispatchEvent(new CustomEvent('charge-created', { detail: data }))
      setTimeout(() => this.close(), 1500)
    },

    async submitRecurring() {
      const body = {
        recurring_charge: {
          who_charged: this.whoCharged,
          who_paid: this.whoPaid,
          property_id: this.selectedProperty?.id || null,
          tenancy_id: this.selectedTenancy?.id || null,
          contractor_id_charged: this.whoCharged === 'contractor' ? this.selectedContractor?.id : null,
          contractor_id_paid: this.selectedPaidContractor?.id || null,
          net_amount_pence: this.netAmountPence,
          vat_amount_pence: this.vatAmountPence,
          description: this.description,
          frequency: this.recurringFrequency,
          start_date: this.recurringStartDate,
          end_date: this.recurringEndDate || null,
          fee_category_id: this.feeCategoryId || null
        }
      }

      const response = await fetch('/accounting/accounts/recurring_charges.json', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': this.csrfToken
        },
        body: JSON.stringify(body)
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Failed to set up recurring charge')
      }

      this.successMessage = 'Recurring charge set up successfully'
      window.dispatchEvent(new CustomEvent('charge-created', { detail: data }))
      setTimeout(() => this.close(), 1500)
    },

    // Computed properties
    get netAmountPence() {
      const pounds = parseFloat(this.netAmountPounds)
      if (isNaN(pounds) || pounds <= 0) return 0
      return Math.round(pounds * 100)
    },

    get vatAmountPence() {
      if (!this.chargesVat) return 0
      const rate = parseFloat(this.vatRate) || 0
      return Math.floor(this.netAmountPence * rate / 100)
    },

    get grossAmountPence() {
      return this.netAmountPence + this.vatAmountPence
    },

    // What the form is still waiting for, named in the words the labels use.
    // The button stays gated on these — a half-filled charge is no use to
    // anyone — but nothing may gate it silently: every entry that can hold the
    // button back has to be able to say so, in the line beside it.
    //
    // Each is listed only once its own field is on screen, so the form never
    // asks for something it hasn't shown you yet.
    //
    // Note what is *not* here. Whether a charge is wise is never a gate: that
    // is advice, it is given next to the field it concerns, and it remains the
    // user's call to overrule.
    get missingFields() {
      const missing = []
      const need = (unmet, label) => { if (unmet) missing.push(label) }

      need(!this.whoCharged, 'who is being charged')
      need(this.whoCharged === 'property' && !this.selectedProperty, 'the property')
      need(this.whoCharged === 'tenancy' && !this.selectedTenancy, 'the tenancy')
      need(this.whoCharged === 'contractor' && !this.selectedContractor, 'the contractor being charged')

      need(this.applicationPrefill && !this.retentionGroundKey, 'the statutory ground you are relying on')

      need(this.showWhoPaid && !this.whoPaid, 'who receives the money')
      need(this.whoPaid === 'contractor' && !this.selectedPaidContractor, 'the contractor being paid')

      need(this.chargeMode === 'one_off' && !this.chargeDate, 'the date of the charge')
      need(this.chargeMode === 'recurring' && !this.recurringStartDate, 'the date it starts')

      need(this.showAmountFields && this.netAmountPence <= 0, 'an amount')
      need(this.showAmountFields && !this.description.trim(), 'a description')

      need(this.showFeeCategory && !this.feeCategoryId, 'a category')

      return missing
    },

    // Not missing — wrong. Retaining more than you hold is arithmetic rather
    // than an omission, so it reads as a correction to make and not as another
    // box to fill.
    get amountConflict() {
      if (!this.applicationPrefill) return null
      if (this.netAmountPence <= 0) return null
      if (this.grossAmountPence <= this.applicationHeldPence) return null

      return `${this.formatPence(this.grossAmountPence)} is more than the ` +
             `${this.formatPence(this.applicationHeldPence)} held on this application — ` +
             'you can only retain money you are holding.'
    },

    // "a, b and c" — the outstanding items read as a sentence rather than a
    // list of failures.
    get missingFieldsSentence() {
      const items = this.missingFields
      if (items.length <= 1) return items.join('')
      return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`
    },

    get canSubmit() {
      return this.missingFields.length === 0 && !this.amountConflict
    },

    // The one source of truth for the category picker: the view shows it off
    // this getter and missingFields demands it off the same one, so the field
    // can never be hidden and required at once — which it was. Fee categories
    // police Schedule 1 permitted payments; a holding-deposit retention is a
    // Schedule 2 matter with no category to give, so the form stopped asking
    // but the button carried on waiting, for ever.
    get showFeeCategory() {
      return this.showAmountFields && this.whoPaid === 'agency' && !this.applicationPrefill
    },

    // Categories visible in the picker for the current whoCharged context.
    // Contractor charges hide service-tier categories — those represent
    // tenancy revenue, not contractor work.
    get visibleFeeCategories() {
      if (this.whoCharged === 'contractor') {
        return this.feeCategories.filter(fc => !fc.service_type_key)
      }
      return this.feeCategories
    },

    get showWhoPaid() {
      if (this.whoCharged === 'tenancy_application') return this.applicationHeldPence > 0
      if (this.whoCharged === 'contractor' && this.selectedContractor) return true
      if (this.whoCharged === 'property' && this.selectedProperty) return true
      if (this.whoCharged === 'tenancy' && this.selectedTenancy) return true
      return false
    },

    get showAmountFields() {
      return this.showWhoPaid && this.whoPaid !== ''
    },

    // "Who will be paid?" is the wrong question when a tenant contributes to
    // something for the landlord — they aren't being billed for a service,
    // the money is passed on. Same control, honest wording per direction.
    get whoPaidLabel() {
      return (this.whoCharged === 'tenancy' || this.whoCharged === 'tenancy_application')
        ? 'Who receives it?' : 'Who will be paid?'
    },

    // The third card: the party on the other side of this property. Charging a
    // landlord, it's the sitting tenants; charging a tenancy, it's the
    // landlords. Derived from the property either way — never searched, so the
    // wrong party can't be picked out of a list of look-alikes.
    get thirdPayee() {
      if (this.whoCharged === 'property') {
        if (!this.propertyTenancy) return null
        return {
          key: 'tenancy',
          title: 'Tenants',
          detail: this.propertyTenancy.tenant_names || 'The sitting tenants'
        }
      }

      if (this.whoCharged === 'tenancy' || this.whoCharged === 'tenancy_application') {
        if (!this.tenancyLandlordNames) return null
        return {
          key: 'landlord',
          title: 'Landlord',
          detail: this.tenancyLandlordNames
        }
      }

      return null
    },

    get selectedRetentionGround() {
      return this.retentionGrounds.find(g => g.key === this.retentionGroundKey) || null
    },

    get isContractorInvoice() {
      return this.whoPaid === 'contractor'
    },

    // The only VAT this form deals in is the agency's own. A supplier invoice
    // is entered gross-as-net, and money moving between a landlord and their
    // tenant carries none at all.
    get chargesVat() {
      return this.whoPaid === 'agency'
    },

    get amountLabel() {
      return this.whoPaid === 'contractor' ? 'Invoice amount (inc. VAT)' : 'Amount'
    },

    get amountHint() {
      if (this.whoPaid === 'contractor') return 'Full amount charged to the landlord(s) and paid to the contractor.'
      if (this.whoPaid === 'tenancy') return 'Charged to the landlord and paid to the tenants.'
      if (this.whoPaid === 'landlord') return 'Charged to the tenancy and passed to the landlord.'
      return ''
    },

    get recurringScheduleText() {
      if (!this.recurringStartDate) return ''
      const date = new Date(this.recurringStartDate + 'T00:00:00')
      if (isNaN(date)) return ''

      if (this.recurringFrequency === 'annual') {
        const day = date.getDate()
        const suffix = day === 1 || day === 21 || day === 31 ? 'st' :
                       day === 2 || day === 22 ? 'nd' :
                       day === 3 || day === 23 ? 'rd' : 'th'
        const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
        return `Charges will be raised on the ${day}${suffix} of ${months[date.getMonth()]} each year`
      } else if (this.recurringFrequency === 'monthly') {
        const day = date.getDate()
        const suffix = day === 1 || day === 21 || day === 31 ? 'st' :
                       day === 2 || day === 22 ? 'nd' :
                       day === 3 || day === 23 ? 'rd' : 'th'
        return `Charges will be raised on the ${day}${suffix} of each month`
      } else {
        const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
        return `Charges will be raised every ${days[date.getDay()]}`
      }
    },

    get csrfToken() {
      return document.querySelector('meta[name="csrf-token"]')?.content
    },

    // Format helpers
    formatPence(pence) {
      if (pence === null || pence === undefined) return '-'
      const absPence = Math.abs(pence)
      const pounds = (absPence / 100).toFixed(2)
      const formatted = parseFloat(pounds).toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })
      return '\u00A3' + formatted
    },

    formatDate(dateStr) {
      if (!dateStr) return '-'
      const date = new Date(dateStr + 'T00:00:00')
      const day = date.getDate()
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${day} ${months[date.getMonth()]} ${date.getFullYear()}`
    },

    chargeTypeBadgeClass(type) {
      if (type === 'fee') return 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400'
      if (type === 'supplier_invoice') return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400'
      if (type === 'rent') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
      if (type === 'deposit') return 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
      return 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    },

    chargeTypeLabel(type) {
      if (type === 'fee') return 'Fee'
      if (type === 'supplier_invoice') return 'Invoice'
      if (type === 'rent') return 'Rent'
      if (type === 'deposit') return 'Deposit'
      if (type === 'rent_refund') return 'Refund'
      return type
    }
  }
}
